/**
 * PortalHome — landing page for the Therapist-Led patient portal.
 *
 * V1 surfaces a "Today" card (next session join), a tasks summary,
 * and a shared summary preview. Data sources will be wired through
 * as Spec D (in-session control surface), Spec E (dual summaries),
 * Spec H (pre-session workflow), and Spec K (session orchestration)
 * land. For now the page renders friendly placeholders.
 *
 * AIMHI Direct clients (user.effectiveEnrolmentStatus set) get three things
 * on top, none of which touch other portal clients:
 *  - the stage is re-read from the server on mount, on window focus and when
 *    the therapist's decision arrives over the socket, so approval no longer
 *    needs a log-out and log-in (8 Sep 2026 onboarding tests);
 *  - the stage card puts the questionnaires before booking until they are done;
 *  - the appointment card says how the call actually starts (the therapist
 *    rings them on screen) instead of showing a join button that never enables.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext, useLocation, Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, Typography, Button, Stack, Box, Chip, Snackbar, Alert } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import EnrolmentStageCard from '../../components/portal/EnrolmentStageCard';
import { useSocket } from '../../context/SocketContext';
import api from '../../services/api';

// An appointment is only 'upcoming' while it is still open: a completed or
// missed call must leave this list and close its waiting room (ST-20260916-0002).
const OPEN_APPOINTMENT_STATUSES = ['scheduled', 'confirmed'];

const FOCUS_REFRESH_MIN_GAP_MS = 20 * 1000;
// Module-level so a remount of this page (the app re-renders its tree when the
// user object changes) cannot restart the mount refresh and loop.
let lastRefreshAt = 0;

function PortalHome() {
  const { user, refreshUser } = useOutletContext();
  const location = useLocation();
  const socket = useSocket();
  const name = user?.name?.split(' ')[0] || 'there';
  const enrolmentStatus = user?.effectiveEnrolmentStatus || null;
  const enrolled = !!enrolmentStatus;

  // --- AIMHI Direct: keep the stage current without a re-login ---------------
  // refreshUser comes from App.js, where it is recreated on every render, so it
  // must never be an effect dependency: on 8 Sep 2026 that produced a render
  // loop after signup (refresh -> setUser -> new function -> refresh). It is
  // held in a ref and the mount refresh runs once per enrolment status.
  const refreshUserRef = useRef(refreshUser);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);
  const refreshInFlight = useRef(false);
  const refreshStage = useCallback(async () => {
    const fn = refreshUserRef.current;
    if (!enrolled || typeof fn !== 'function' || refreshInFlight.current) return;
    refreshInFlight.current = true;
    lastRefreshAt = Date.now();
    try { await fn(); } catch (_) { /* stale stage is better than an error */ }
    finally { refreshInFlight.current = false; }
  }, [enrolled]);

  // Once per stage: the login-time user object may already be behind.
  useEffect(() => {
    if (!enrolled) return;
    if (Date.now() - lastRefreshAt > FOCUS_REFRESH_MIN_GAP_MS) refreshStage();
  }, [enrolled, enrolmentStatus, refreshStage]);

  useEffect(() => {
    if (!enrolled) return undefined;
    const onFocus = () => {
      if (Date.now() - lastRefreshAt > FOCUS_REFRESH_MIN_GAP_MS) refreshStage();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enrolled, refreshStage]);

  const [notice, setNotice] = useState(null); // { severity, text }
  useEffect(() => {
    if (!enrolled || !socket) return undefined;
    const onApproved = () => {
      setNotice({ severity: 'success', text: 'Your therapist has approved your place. Your programme starts now.' });
      refreshStage();
    };
    const onUpdated = () => refreshStage();
    socket.on('enrolment:approved', onApproved);
    socket.on('enrolment:updated', onUpdated);
    return () => {
      socket.off('enrolment:approved', onApproved);
      socket.off('enrolment:updated', onUpdated);
    };
  }, [enrolled, socket, refreshStage]);

  useEffect(() => {
    if (location.state?.intakeTasksComplete) {
      setNotice({ severity: 'success', text: 'Questionnaires done. The next step is to book your first appointment.' });
    }
  }, [location.state]);

  // --- AIMHI Direct: are the intake questionnaires finished? -----------------
  const [intakeComplete, setIntakeComplete] = useState(null);
  useEffect(() => {
    if (enrolmentStatus !== 'pending_intake') { setIntakeComplete(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/pre-session/me/assignments', localStorage.getItem('token'));
        if (cancelled) return;
        const assignments = res.data?.assignments || [];
        let seen = 0;
        let done = true;
        for (const a of assignments) {
          const byId = new Map((a.taskCompletions || []).map(c => [String(c.taskId), c]));
          for (const t of (a.templateSnapshot?.tasks || [])) {
            if (t.required === false) continue;
            seen += 1;
            if (byId.get(String(t._id))?.status !== 'completed') done = false;
          }
        }
        setIntakeComplete(seen > 0 ? done : null);
      } catch (_) { setIntakeComplete(null); }
    })();
    return () => { cancelled = true; };
  }, [enrolmentStatus]);

  // The client's next appointment. Without this the portal showed "your
  // therapist hasn't scheduled a session yet" to someone who had just booked
  // one, which reads as though the booking failed.
  const [appointment, setAppointment] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/appointments/me', localStorage.getItem('token'));
        if (cancelled) return;
        const upcoming = (res.data?.appointments || [])
          .filter(a => OPEN_APPOINTMENT_STATUSES.includes(a.status) && new Date(a.scheduledEnd || a.scheduledStart) > new Date())
          .sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart));
        setAppointment(upcoming[0] || null);
      } catch (_) { /* the card falls back to its empty state */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Minute tick so the countdown on the appointment card stays honest.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!appointment) return undefined;
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, [enrolled, appointment]);

  const therapistName = (t) =>
    t?.therapistInfo?.displayName || [t?.firstName, t?.lastName].filter(Boolean).join(' ') || t?.name || 'your therapist';
  const when = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
  });

  const minutesToGo = appointment ? Math.round((new Date(appointment.scheduledStart).getTime() - now) / 60000) : null;
  const countdownLabel = () => {
    if (minutesToGo === null) return null;
    if (minutesToGo <= 0) return 'Starting now';
    if (minutesToGo < 60) return `Starts in ${minutesToGo} min`;
    if (minutesToGo < 24 * 60) return `Starts in ${Math.round(minutesToGo / 60)} h`;
    return `In ${Math.round(minutesToGo / (24 * 60))} days`;
  };
  // Waiting room (ST-20260915-0005): open from 10 minutes before until the end.
  const waitingOpen = !!appointment && minutesToGo !== null && minutesToGo <= 10
    && new Date(appointment.scheduledEnd || appointment.scheduledStart).getTime() > now;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>Hello, {name}</Typography>
        {!enrolmentStatus && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Your therapist is preparing for your next session.
          </Typography>
        )}
      </Box>

      {/* AD-03: every pre-approval stage renders this same portal, so the stage
          card is what distinguishes "consent", "book", "waiting for your
          appointment" and "waiting on your therapist". Renders nothing for a
          client with no programme enrolment, leaving the portal as it was. */}
      <EnrolmentStageCard status={enrolmentStatus} intakeComplete={intakeComplete} />

      <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Chip label={appointment ? 'Booked' : 'Next session'} color="primary" size="small" />
              {appointment ? (
                <>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {when(appointment.scheduledStart)}
                  </Typography>
                  {enrolled ? (
                    <Typography variant="body2" color="text.secondary">
                      With {therapistName(appointment.therapistId)}. From 10 minutes before the time, press Join
                      the waiting room to check your camera and microphone and wait there. The call connects when
                      {' '}{therapistName(appointment.therapistId)} starts it. Use a laptop or desktop with a camera
                      and microphone if you can.
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      With {therapistName(appointment.therapistId)}. You will get a reminder beforehand,
                      and the join button becomes active at the start of your appointment.
                    </Typography>
                  )}
                  <Button component={RouterLink} to="/appointments/book" size="small" sx={{ mt: 1, pl: 0 }}>
                    View or change this appointment
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mt: 1 }}>Next session</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {enrolled
                      ? "You do not have an appointment booked yet. Once you do, the details will show here and your therapist will ring you on screen at the time."
                      : "You do not have an appointment booked yet. You'll see the details and a join link here once you do."}
                  </Typography>
                </>
              )}
            </Box>
            {waitingOpen ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<VideocamIcon />}
                component={RouterLink}
                to={`/appointments/${appointment._id}/waiting`}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
              >
                Join the waiting room
              </Button>
            ) : enrolled ? (
              appointment && (
                <Chip
                  icon={<VideocamIcon />}
                  color={minutesToGo !== null && minutesToGo <= 15 ? 'success' : 'default'}
                  label={countdownLabel()}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, fontWeight: 600 }}
                />
              )
            ) : (
              <Button
                variant="contained"
                size="large"
                startIcon={<VideocamIcon />}
                disabled
                sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
              >
                Join call
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6">Tasks</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Forms or screeners your therapist has asked you to complete before your session.
            </Typography>
            <Button component={RouterLink} to="/portal/tasks" size="small">View tasks</Button>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon color="primary" />
              <Typography variant="h6">Session summaries</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Plain-language recaps your therapist has chosen to share with you.
            </Typography>
            <Button component={RouterLink} to="/portal/summaries" size="small">View summaries</Button>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={!!notice}
        autoHideDuration={8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert> : null}
      </Snackbar>
    </Stack>
  );
}

export default PortalHome;
